// src/components/rpa/RemediationPanel.js
// ⭐ NEW (Aug 2026) — Remediation tracker UI for the Client & Issues dashboard.
//
// Self-contained so ClientsPanel.js needs only an import plus swapping the body
// of its existing "Remediation" tab — no other changes to that file.
//
// Backs onto the trackable-remediation endpoints:
//   POST   /admin/rpa/docs/:docId/issues/:issueId/remediation-actions
//   PATCH  /admin/rpa/docs/:docId/issues/:issueId/remediation-actions/:actionId
//   DELETE /admin/rpa/docs/:docId/issues/:issueId/remediation-actions/:actionId
//   POST   /admin/rpa/docs/:docId/issues/:issueId/remediation/assert
//   POST   /admin/rpa/docs/:docId/issues/:issueId/remediation/verify
//   PATCH  /admin/rpa/docs/:docId/issues/:issueId/remediation-plan     (existing)
//   PATCH  /admin/rpa/docs/:docId/issues/:issueId/management-response  (existing)
//   POST   /admin/rpa/docs/:docId/issues/:issueId/close                (existing, now gated)
//
// The four remediation stages come from the SERVER (issue.remediationStage on
// the client rollup) rather than being re-derived here, so the UI and the
// closure gate can never disagree about whether an issue is ready to close.
//
// Props:
//   issues        full issue docs from GET /admin/rpa/clients/:clientId/issues
//   remediation   summary.remediation from that same response (may be undefined
//                 on an older backend — the KPI band degrades gracefully)
//   onChanged     called after any successful mutation so the parent refetches
//   currentAdminId  optional; used to warn before attempting a self-verification

import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const SEVERITY_ORDER = ['critical', 'high', 'moderate', 'low'];

const SEVERITY_COLORS = {
  critical: { bg: '#fef2f2', border: '#ef4444', text: '#991b1b', solid: '#ef4444' },
  high:     { bg: '#fff7ed', border: '#f97316', text: '#9a3412', solid: '#f97316' },
  moderate: { bg: '#fefce8', border: '#eab308', text: '#854d0e', solid: '#eab308' },
  low:      { bg: '#f0f9ff', border: '#38bdf8', text: '#075985', solid: '#38bdf8' }
};

const ACTION_STATUS = {
  not_started: { label: 'Not started', color: '#9ca3af', bg: '#f3f4f6' },
  in_progress: { label: 'In progress', color: '#1e40af', bg: '#eff6ff' },
  completed:   { label: 'Completed',   color: '#065f46', bg: '#f0fdf4' },
  blocked:     { label: 'Blocked',     color: '#991b1b', bg: '#fef2f2' },
  cancelled:   { label: 'Cancelled',   color: '#4b5563', bg: '#f3f4f6' }
};

const STAGE_LABELS = ['Response', 'Plan', 'Asserted', 'Verified'];

const fmtDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return '-'; }
};

const fmtMoney = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return null;
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

const titleCase = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const dateInputValue = (d) => {
  if (!d) return '';
  try { return new Date(d).toISOString().slice(0, 10); } catch { return ''; }
};

// Derive the stage locally only if the server didn't supply it (older backend).
const stageOf = (i) =>
  i.remediationStage || {
    hasResponse: !!i.managementResponse?.text,
    hasPlan: !!i.remediationPlan?.text,
    asserted: !!i.remediationPlan?.assertedAt,
    verified: i.verification?.status === 'verified',
    completed: [
      !!i.managementResponse?.text,
      !!i.remediationPlan?.text,
      !!i.remediationPlan?.assertedAt,
      i.verification?.status === 'verified'
    ].filter(Boolean).length,
    total: 4
  };

const RemediationPanel = ({ issues = [], remediation = null, onChanged, currentAdminId = null }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(null);         // action key currently in flight
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [blockers, setBlockers] = useState(null); // { issueId, list[] } from a 409
  const [filter, setFilter] = useState('all');    // all | overdue | awaiting | unaddressed

  // per-issue draft state, keyed by issue id
  const [drafts, setDrafts] = useState({});

  const openIssues = issues.filter((i) => i.isOpen !== false && i.status !== 'closed' && i.status !== 'voided');

  const draftFor = (issue) => drafts[issue._id] || {
    response: issue.managementResponse?.text || '',
    planText: issue.remediationPlan?.text || '',
    planTarget: dateInputValue(issue.remediationPlan?.targetDate || issue.dueDate),
    newAction: '',
    newActionTarget: '',
    assertionNote: '',
    assertedByName: '',
    procedures: '',
    evidenceRef: '',
    verifyNotes: '',
    rejectionReason: '',
    closureNotes: '',
    overrideJustification: ''
  };

  const setDraft = (id, patch) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || draftFor(issues.find((x) => x._id === id) || {})), ...patch } }));

  // ── API plumbing ──────────────────────────────────────────────────────────
  const call = async (key, fn, successMsg) => {
    setBusy(key);
    setError('');
    setNotice('');
    setBlockers(null);
    try {
      const res = await fn();
      if (successMsg) setNotice(successMsg);
      if (typeof onChanged === 'function') await onChanged();
      return res?.data || null;
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === 'CLOSURE_BLOCKED') {
        setBlockers({ issueId: key.split(':')[1], list: data.blockers || [] });
        setError(data.message || 'Issue cannot be closed yet.');
      } else if (data?.outstandingActions?.length) {
        setError(`${data.error} — ${data.outstandingActions.map((a) => a.description).join('; ')}`);
      } else {
        setError(data?.error || data?.message || err.message || 'Request failed');
      }
      return null;
    } finally {
      setBusy(null);
    }
  };

  const base = (i) => `${API_URL}/admin/rpa/docs/${i.docId}/issues/${i._id}`;
  const cfg = { withCredentials: true };

  const saveResponse = (i) => {
    const d = draftFor(i);
    return call(`resp:${i._id}`, () => axios.patch(`${base(i)}/management-response`, { text: d.response }, cfg), 'Management response saved.');
  };

  const savePlan = (i) => {
    const d = draftFor(i);
    return call(`plan:${i._id}`, () => axios.patch(`${base(i)}/remediation-plan`, {
      text: d.planText,
      targetDate: d.planTarget || null
    }, cfg), 'Remediation plan saved.');
  };

  const addAction = (i) => {
    const d = draftFor(i);
    if (!d.newAction.trim()) { setError('Describe the corrective action first.'); return; }
    return call(`addact:${i._id}`, () => axios.post(`${base(i)}/remediation-actions`, {
      description: d.newAction.trim(),
      targetDate: d.newActionTarget || null
    }, cfg), 'Corrective action added.').then((r) => {
      if (r) setDraft(i._id, { newAction: '', newActionTarget: '' });
      return r;
    });
  };

  const setActionStatus = (i, action, status) =>
    call(`act:${action._id}`, () => axios.patch(`${base(i)}/remediation-actions/${action._id}`, { status }, cfg),
      `Action marked ${ACTION_STATUS[status]?.label.toLowerCase() || status}.`);

  const removeAction = (i, action) => {
    if (!window.confirm('Remove this corrective action?')) return;
    return call(`delact:${action._id}`, () => axios.delete(`${base(i)}/remediation-actions/${action._id}`, cfg), 'Corrective action removed.');
  };

  const assertComplete = (i) => {
    const d = draftFor(i);
    if (!window.confirm('Record that management asserts this remediation is complete?\n\nThis moves the issue to Pending Review and requires a different, more senior auditor to verify it.')) return;
    return call(`assert:${i._id}`, () => axios.post(`${base(i)}/remediation/assert`, {
      assertionNote: d.assertionNote,
      assertedByName: d.assertedByName,
      evidenceRef: d.evidenceRef || null
    }, cfg), 'Assertion recorded. Awaiting independent verification.');
  };

  const verify = (i, outcome) => {
    const d = draftFor(i);
    const asserterId = i.remediationPlan?.assertedBy?.adminId;
    if (outcome === 'verified' && currentAdminId && asserterId === currentAdminId) {
      setError('You recorded the assertion for this issue, so you cannot verify it. Verification must be performed by a different auditor.');
      return;
    }
    if (outcome === 'verified' && !d.procedures.trim()) {
      setError('Document the procedures performed before verifying.');
      return;
    }
    if (outcome === 'rejected' && !d.rejectionReason.trim()) {
      setError('A rejection reason is required.');
      return;
    }
    return call(`verify:${i._id}`, () => axios.post(`${base(i)}/remediation/verify`, {
      outcome,
      proceduresPerformed: d.procedures,
      evidenceRef: d.evidenceRef || null,
      notes: d.verifyNotes,
      rejectionReason: d.rejectionReason
    }, cfg), outcome === 'verified' ? 'Remediation verified. The issue can now be closed.' : 'Assertion rejected and returned for further action.');
  };

  const closeIssue = (i, override = false) => {
    const d = draftFor(i);
    if (override && !d.overrideJustification.trim()) {
      setError('A written justification is required to close without verified remediation.');
      return;
    }
    return call(`close:${i._id}`, () => axios.post(`${base(i)}/close`, {
      closureNotes: d.closureNotes,
      overrideVerification: override,
      overrideJustification: d.overrideJustification
    }, cfg), 'Issue closed.');
  };

  // ── filtering & ordering ──────────────────────────────────────────────────
  const visible = openIssues.filter((i) => {
    const s = stageOf(i);
    if (filter === 'overdue') return !!i.isOverdue;
    if (filter === 'awaiting') return i.status === 'pending_review' && !s.verified;
    if (filter === 'unaddressed') return !s.hasResponse && !s.hasPlan;
    return true;
  });

  const ordered = [...visible].sort((a, b) => {
    const od = (b.isOverdue ? 1 : 0) - (a.isOverdue ? 1 : 0);
    if (od) return od;
    const aw = ((b.status === 'pending_review') ? 1 : 0) - ((a.status === 'pending_review') ? 1 : 0);
    if (aw) return aw;
    const sv = SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity);
    if (sv) return sv;
    return new Date(a.raisedAt || a.createdAt) - new Date(b.raisedAt || b.createdAt);
  });

  // ── KPI band (server-computed when available) ─────────────────────────────
  const r = remediation || null;
  const kpis = r ? [
    { label: 'Fully staged', value: r.staged, accent: '#22c55e', tone: '#065f46' },
    { label: 'Awaiting verification', value: r.awaitingVerification, accent: '#3b82f6', tone: '#1e40af' },
    { label: 'Unaddressed', value: r.unaddressed, accent: '#ef4444', tone: r.unaddressed ? '#b91c1c' : '#111827' },
    { label: 'Overdue', value: r.overdue, accent: r.overdue ? '#ef4444' : '#e5e7eb', tone: r.overdue ? '#b91c1c' : '#111827' },
    { label: 'Rejected', value: r.rejected, accent: '#f97316', tone: r.rejected ? '#9a3412' : '#111827' }
  ] : [];

  if (!openIssues.length) {
    return <div style={emptyBox}>No open issues — nothing to remediate. 🎉</div>;
  }

  return (
    <div>
      <style>{`
        .rp-row { transition: transform .12s ease, box-shadow .12s ease; }
        .rp-row:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(17,24,39,.08); }
        .rp-bar { transition: width .5s cubic-bezier(.4,0,.2,1); }
      `}</style>

      {/* KPI band */}
      {r && (
        <div style={kpiRow}>
          {kpis.map((k) => (
            <div key={k.label} style={{ ...kpiCard, borderTopColor: k.accent }}>
              <div style={{ ...kpiNum, color: k.tone }}>{k.value ?? 0}</div>
              <div style={kpiLabel}>{k.label}</div>
            </div>
          ))}
          <div style={{ ...kpiCard, borderTopColor: '#8b5cf6' }}>
            <div style={kpiNum}>{r.coveragePct ?? 0}%</div>
            <div style={kpiLabel}>Coverage</div>
          </div>
        </div>
      )}

      {r && r.overdue > 0 && (
        <div style={alertBox}>
          ⏰ <strong>{r.overdue}</strong> open issue{r.overdue === 1 ? '' : 's'} past the remediation target date
          {r.overdueCritical > 0 && <> — <strong>{r.overdueCritical}</strong> critical</>}
          {r.maxDaysOverdue > 0 && <> · longest {r.maxDaysOverdue} days</>}
          {fmtMoney(r.overdueExposure) && r.overdueExposure > 0 && <> · {fmtMoney(r.overdueExposure)} exposure</>}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 6, margin: '14px 0', flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          ['all', `All open (${openIssues.length})`],
          ['overdue', `Overdue (${openIssues.filter((i) => i.isOverdue).length})`],
          ['awaiting', `Awaiting verification (${openIssues.filter((i) => i.status === 'pending_review').length})`],
          ['unaddressed', `Unaddressed (${openIssues.filter((i) => { const s = stageOf(i); return !s.hasResponse && !s.hasPlan; }).length})`]
        ].map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)} style={filter === key ? chipActive : chip}>
            {label}
          </button>
        ))}
      </div>

      {error && <div style={errorBox}>{error}</div>}
      {notice && <div style={noticeBox}>{notice}</div>}

      {!ordered.length && <div style={emptyBox}>No issues match this filter.</div>}

      {/* Issue rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {ordered.map((i) => {
          const sev = SEVERITY_COLORS[i.severity] || SEVERITY_COLORS.low;
          const s = stageOf(i);
          const expanded = expandedId === String(i._id);
          const d = draftFor(i);
          const actions = i.remediationActions || [];
          const liveActions = actions.filter((a) => a.status !== 'cancelled');
          const allDone = liveActions.length > 0 && liveActions.every((a) => a.status === 'completed');
          const rejected = i.verification?.status === 'rejected';
          const asserterId = i.remediationPlan?.assertedBy?.adminId;
          const wouldSelfVerify = currentAdminId && asserterId && asserterId === currentAdminId;

          return (
            <div
              key={i._id}
              className="rp-row"
              style={{ ...card, borderLeft: `4px solid ${sev.solid}`, background: expanded ? '#fcfcfd' : '#fff' }}
            >
              {/* Header */}
              <div
                onClick={() => setExpandedId(expanded ? null : String(i._id))}
                style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, cursor: 'pointer' }}
              >
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ color: '#9ca3af', fontWeight: 700 }}>{expanded ? '▾' : '▸'}</span>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>
                      {i.issueNumber ? `#${i.issueNumber} · ` : ''}{i.title}
                    </span>
                    <span style={{ ...pill, background: sev.bg, borderColor: sev.border, color: sev.text }}>
                      {titleCase(i.severity)}
                    </span>
                    {i.isOverdue && (
                      <span style={{ ...pill, background: '#fef2f2', borderColor: '#ef4444', color: '#991b1b' }}>
                        ⏰ {i.daysOverdue != null ? `${i.daysOverdue}d overdue` : 'Overdue'}
                      </span>
                    )}
                    {i.status === 'pending_review' && (
                      <span style={{ ...pill, background: '#eff6ff', borderColor: '#3b82f6', color: '#1e40af' }}>
                        Awaiting verification
                      </span>
                    )}
                    {rejected && (
                      <span style={{ ...pill, background: '#fff7ed', borderColor: '#f97316', color: '#9a3412' }}>
                        Returned ×{i.verification?.rejectionCount || 1}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                    {i.projectName || '-'}
                    {i.workpaperRef ? ` · ${i.workpaperRef}` : ''}
                    {i.owner?.name ? ` · Owner: ${i.owner.name}` : ' · Unassigned'}
                    {i.effectiveDueDate ? ` · Due ${fmtDate(i.effectiveDueDate)}` : ''}
                    {fmtMoney(i.amount) ? ` · ${fmtMoney(i.amount)}` : ''}
                  </div>
                </div>

                {/* 4-stage pipeline */}
                <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                  {STAGE_LABELS.map((label, idx) => {
                    const done = [s.hasResponse, s.hasPlan, s.asserted, s.verified][idx];
                    const prevDone = idx > 0 ? [s.hasResponse, s.hasPlan, s.asserted, s.verified][idx - 1] : true;
                    return (
                      <React.Fragment key={label}>
                        {idx > 0 && (
                          <div style={{ width: 24, height: 2, background: prevDone && done ? '#22c55e' : '#e5e7eb' }} />
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                          <div style={{
                            width: 20, height: 20, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 800,
                            background: done ? '#22c55e' : '#fff',
                            border: done ? '2px solid #22c55e' : '2px solid #d1d5db',
                            color: done ? '#fff' : '#9ca3af'
                          }}>
                            {done ? '✓' : ''}
                          </div>
                          <div style={{ fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap', color: done ? '#065f46' : '#9ca3af' }}>
                            {label}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>

                <div style={{ flex: '0 0 auto', textAlign: 'right', minWidth: 56 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: s.completed === 4 ? '#065f46' : '#111827' }}>
                    {s.completed}/4
                  </div>
                  <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>Stages</div>
                </div>
              </div>

              {/* Expanded working area */}
              {expanded && (
                <div style={drawer}>
                  {rejected && i.verification?.rejectionReason && (
                    <div style={rejectBox}>
                      <strong>Previous assertion rejected:</strong> {i.verification.rejectionReason}
                      {i.verification.verifiedBy?.name && <> — {i.verification.verifiedBy.name}</>}
                    </div>
                  )}

                  {/* Stage 1 — management response */}
                  <section style={block}>
                    <div style={blockTitle}>1 · Management response</div>
                    <textarea
                      value={d.response}
                      onChange={(e) => setDraft(i._id, { response: e.target.value })}
                      placeholder="Management's response to the finding…"
                      rows={2}
                      style={textarea}
                    />
                    <button onClick={() => saveResponse(i)} disabled={busy === `resp:${i._id}`} style={smallPrimary}>
                      {busy === `resp:${i._id}` ? 'Saving…' : 'Save response'}
                    </button>
                    {i.managementResponse?.respondedAt && (
                      <span style={meta}>Recorded {fmtDate(i.managementResponse.respondedAt)}</span>
                    )}
                  </section>

                  {/* Stage 2 — plan + corrective actions */}
                  <section style={block}>
                    <div style={blockTitle}>2 · Remediation plan</div>
                    <textarea
                      value={d.planText}
                      onChange={(e) => setDraft(i._id, { planText: e.target.value })}
                      placeholder="Corrective action plan narrative…"
                      rows={2}
                      style={textarea}
                    />
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={meta}>Target date</label>
                      <input
                        type="date"
                        value={d.planTarget}
                        onChange={(e) => setDraft(i._id, { planTarget: e.target.value })}
                        style={input}
                      />
                      <button onClick={() => savePlan(i)} disabled={busy === `plan:${i._id}`} style={smallPrimary}>
                        {busy === `plan:${i._id}` ? 'Saving…' : 'Save plan'}
                      </button>
                    </div>

                    {/* corrective actions */}
                    <div style={{ marginTop: 12 }}>
                      <div style={{ ...blockTitle, fontSize: 11 }}>Corrective actions</div>
                      {actions.length === 0 && (
                        <div style={{ ...meta, fontStyle: 'italic' }}>
                          None yet — the plan narrative alone is enough, but discrete actions are trackable.
                        </div>
                      )}
                      {actions.map((a) => {
                        const st = ACTION_STATUS[a.status] || ACTION_STATUS.not_started;
                        return (
                          <div key={a._id} style={actionRow}>
                            <span style={{ ...pill, background: st.bg, color: st.color, borderColor: 'transparent' }}>
                              {st.label}
                            </span>
                            <span style={{ flex: 1, fontSize: 13, color: '#1f2937', minWidth: 160 }}>
                              {a.description}
                              {a.targetDate && <span style={meta}> · due {fmtDate(a.targetDate)}</span>}
                              {a.completedAt && <span style={{ ...meta, color: '#065f46' }}> · done {fmtDate(a.completedAt)}</span>}
                            </span>
                            <select
                              value={a.status}
                              onChange={(e) => setActionStatus(i, a, e.target.value)}
                              disabled={busy === `act:${a._id}`}
                              style={{ ...input, padding: '4px 6px', fontSize: 12 }}
                            >
                              {Object.entries(ACTION_STATUS).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                              ))}
                            </select>
                            {!a.completedAt && (
                              <button
                                onClick={() => removeAction(i, a)}
                                disabled={busy === `delact:${a._id}`}
                                title="Remove"
                                style={{ ...smallSecondary, color: '#9ca3af', padding: '4px 8px' }}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        );
                      })}
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <input
                          value={d.newAction}
                          onChange={(e) => setDraft(i._id, { newAction: e.target.value })}
                          placeholder="Add a corrective action…"
                          style={{ ...input, flex: '1 1 240px' }}
                        />
                        <input
                          type="date"
                          value={d.newActionTarget}
                          onChange={(e) => setDraft(i._id, { newActionTarget: e.target.value })}
                          style={input}
                        />
                        <button onClick={() => addAction(i)} disabled={busy === `addact:${i._id}`} style={smallSecondary}>
                          {busy === `addact:${i._id}` ? 'Adding…' : '+ Add'}
                        </button>
                      </div>
                    </div>
                  </section>

                  {/* Stage 3 — assertion */}
                  <section style={block}>
                    <div style={blockTitle}>3 · Completion assertion</div>
                    {s.asserted ? (
                      <div style={{ ...meta, color: '#065f46' }}>
                        ✓ Asserted complete {fmtDate(i.remediationPlan.assertedAt)}
                        {i.remediationPlan.assertedBy?.name && ` — recorded by ${i.remediationPlan.assertedBy.name}`}
                        {i.remediationPlan.assertionNote && <div style={{ color: '#374151', marginTop: 4 }}>{i.remediationPlan.assertionNote}</div>}
                      </div>
                    ) : (
                      <>
                        {!s.hasPlan && <div style={{ ...meta, color: '#9a3412' }}>A remediation plan must be saved before completion can be asserted.</div>}
                        {liveActions.length > 0 && !allDone && (
                          <div style={{ ...meta, color: '#9a3412' }}>
                            {liveActions.filter((a) => a.status !== 'completed').length} corrective action(s) not yet completed.
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                          <input
                            value={d.assertedByName}
                            onChange={(e) => setDraft(i._id, { assertedByName: e.target.value })}
                            placeholder="Client contact who asserted (optional)"
                            style={{ ...input, flex: '1 1 220px' }}
                          />
                          <input
                            value={d.evidenceRef}
                            onChange={(e) => setDraft(i._id, { evidenceRef: e.target.value })}
                            placeholder="Evidence ref (e.g. WP-300)"
                            style={{ ...input, width: 180 }}
                          />
                        </div>
                        <textarea
                          value={d.assertionNote}
                          onChange={(e) => setDraft(i._id, { assertionNote: e.target.value })}
                          placeholder="What management states was done…"
                          rows={2}
                          style={textarea}
                        />
                        <button
                          onClick={() => assertComplete(i)}
                          disabled={busy === `assert:${i._id}` || !s.hasPlan || (liveActions.length > 0 && !allDone)}
                          style={smallPrimary}
                        >
                          {busy === `assert:${i._id}` ? 'Recording…' : '🔖 Assert complete'}
                        </button>
                      </>
                    )}
                  </section>

                  {/* Stage 4 — independent verification */}
                  <section style={block}>
                    <div style={blockTitle}>4 · Independent verification</div>
                    {s.verified ? (
                      <div style={{ ...meta, color: '#065f46' }}>
                        ✓ Verified {fmtDate(i.verification.verifiedAt)} by {i.verification.verifiedBy?.name || 'auditor'}
                        {i.verification.proceduresPerformed && (
                          <div style={{ color: '#374151', marginTop: 4 }}>{i.verification.proceduresPerformed}</div>
                        )}
                        {i.verification.evidenceRef && <div style={meta}>Evidence: {i.verification.evidenceRef}</div>}
                      </div>
                    ) : !s.asserted ? (
                      <div style={{ ...meta, fontStyle: 'italic' }}>Nothing to verify until completion is asserted.</div>
                    ) : (
                      <>
                        {wouldSelfVerify && (
                          <div style={rejectBox}>
                            You recorded the assertion for this issue, so a different auditor must verify it.
                          </div>
                        )}
                        <textarea
                          value={d.procedures}
                          onChange={(e) => setDraft(i._id, { procedures: e.target.value })}
                          placeholder="Procedures performed to confirm the assertion (required to verify)…"
                          rows={2}
                          style={textarea}
                        />
                        <textarea
                          value={d.rejectionReason}
                          onChange={(e) => setDraft(i._id, { rejectionReason: e.target.value })}
                          placeholder="Reason for rejection (required to reject)…"
                          rows={2}
                          style={textarea}
                        />
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            onClick={() => verify(i, 'verified')}
                            disabled={busy === `verify:${i._id}` || wouldSelfVerify}
                            style={{ ...smallPrimary, background: '#16a34a' }}
                          >
                            {busy === `verify:${i._id}` ? 'Submitting…' : '✓ Verify'}
                          </button>
                          <button
                            onClick={() => verify(i, 'rejected')}
                            disabled={busy === `verify:${i._id}`}
                            style={{ ...smallSecondary, borderColor: '#f97316', color: '#9a3412' }}
                          >
                            ✕ Reject and return
                          </button>
                        </div>
                        <div style={meta}>Requires Senior Auditor or above.</div>
                      </>
                    )}
                  </section>

                  {/* Closure */}
                  <section style={{ ...block, borderBottom: 'none', paddingBottom: 0 }}>
                    <div style={blockTitle}>Close issue</div>
                    {blockers && blockers.issueId === String(i._id) && (
                      <div style={rejectBox}>
                        <strong>Cannot close yet:</strong>
                        <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                          {blockers.list.map((b, idx) => <li key={idx} style={{ fontSize: 12 }}>{b}</li>)}
                        </ul>
                      </div>
                    )}
                    <input
                      value={d.closureNotes}
                      onChange={(e) => setDraft(i._id, { closureNotes: e.target.value })}
                      placeholder="Closure notes (optional)"
                      style={{ ...input, width: '100%', boxSizing: 'border-box' }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        onClick={() => closeIssue(i, false)}
                        disabled={busy === `close:${i._id}`}
                        style={{ ...smallPrimary, background: s.verified ? undefined : '#9ca3af' }}
                      >
                        {busy === `close:${i._id}` ? 'Closing…' : 'Close issue'}
                      </button>
                      {!s.verified && (
                        <span style={meta}>Requires verified remediation, or an override below.</span>
                      )}
                    </div>
                    {!s.verified && (
                      <details style={{ marginTop: 8 }}>
                        <summary style={{ fontSize: 12, color: '#9a3412', cursor: 'pointer', fontWeight: 600 }}>
                          Close without verified remediation (override)
                        </summary>
                        <div style={{ marginTop: 6 }}>
                          <div style={meta}>
                            For issues withdrawn on further evidence, findings superseded, or risk formally accepted.
                            Recorded on the issue and visible to reviewers. Audit Manager or above.
                          </div>
                          <textarea
                            value={d.overrideJustification}
                            onChange={(e) => setDraft(i._id, { overrideJustification: e.target.value })}
                            placeholder="Written justification (required)…"
                            rows={2}
                            style={textarea}
                          />
                          <button
                            onClick={() => closeIssue(i, true)}
                            disabled={busy === `close:${i._id}`}
                            style={{ ...smallSecondary, borderColor: '#dc2626', color: '#991b1b' }}
                          >
                            Override and close
                          </button>
                        </div>
                      </details>
                    )}
                  </section>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 10 }}>
        Sorted: overdue first, then awaiting verification, then severity · Click a row to work the issue
      </div>
    </div>
  );
};

// ------------------------------------------------------------------- styles
const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 16px' };
const drawer = { marginTop: 14, paddingTop: 12, borderTop: '1px dashed #e5e7eb', display: 'flex', flexDirection: 'column', gap: 4 };
const block = { paddingBottom: 12, marginBottom: 4, borderBottom: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: 6 };
const blockTitle = { fontSize: 12, fontWeight: 800, color: '#111827', textTransform: 'uppercase', letterSpacing: 0.4 };
const meta = { fontSize: 11, color: '#6b7280' };
const input = { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, outline: 'none', background: '#fff' };
const textarea = { ...input, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' };
const smallPrimary = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6,
  fontSize: 12, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start'
};
const smallSecondary = {
  background: '#fff', color: '#374151', border: '1px solid #d1d5db',
  padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer'
};
const pill = { display: 'inline-block', padding: '2px 8px', borderRadius: 999, border: '1px solid #e5e7eb', fontSize: 11, fontWeight: 700 };
const chip = { ...pill, cursor: 'pointer', background: '#fff', color: '#4b5563', padding: '5px 12px', fontSize: 12 };
const chipActive = { ...chip, background: '#111827', color: '#fff', borderColor: '#111827' };
const actionRow = {
  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
  padding: '6px 8px', border: '1px solid #f3f4f6', borderRadius: 8, marginTop: 6, background: '#fafafa'
};
const kpiRow = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 };
const kpiCard = { background: '#fff', border: '1px solid #e5e7eb', borderTop: '4px solid #6366f1', borderRadius: 12, padding: '12px 14px' };
const kpiNum = { fontSize: 24, fontWeight: 800, color: '#111827', lineHeight: 1.1 };
const kpiLabel = { fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4, fontWeight: 700 };
const emptyBox = { padding: 24, textAlign: 'center', color: '#6b7280', background: '#fff', border: '1px dashed #e5e7eb', borderRadius: 12 };
const errorBox = { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 13 };
const noticeBox = { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#065f46', padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 13 };
const alertBox = { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginTop: 12, fontSize: 13 };
const rejectBox = { background: '#fff7ed', border: '1px solid #fdba74', color: '#9a3412', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 8 };

export default RemediationPanel;